import { useState } from 'react';
import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { User } from '../models/User';

export interface FormData {
  sharePrice: string;
  numberOfShares: string;
}

export interface UseMarketplaceFormsReturn {
  // Sale form state
  showBuyForm: boolean;
  showConfirmForm: boolean;
  formData: FormData;
  legalConfirmation: boolean;
  accuracyConfirmation: boolean;
  
  // Buy request form state
  showRequestToBuyForm: boolean;
  showRequestConfirmForm: boolean;
  requestData: FormData;
  requestFundsConfirmation: boolean;
  requestAccuracyConfirmation: boolean;
  
  // Actions
  openSaleForm: () => void;
  openBuyRequestForm: () => void;
  closeForms: () => void;
  
  // Form handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRequestInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmitInitialForm: (e: React.FormEvent) => void;
  handleSubmitRequestForm: (e: React.FormEvent) => void;
  
  // Confirmation handlers
  setLegalConfirmation: (value: boolean) => void;
  setAccuracyConfirmation: (value: boolean) => void;
  setRequestFundsConfirmation: (value: boolean) => void;
  setRequestAccuracyConfirmation: (value: boolean) => void;
  
  // Final submission
  submitSaleOffer: (user: User, onAdd: (offer: SaleOffer) => Promise<void>) => void;
  submitBuyRequest: (user: User, onAdd: (request: BuyRequest) => Promise<void>) => void;
  
  // Reset functions
  resetForms: () => void;
}

export const useMarketplaceForms = (): UseMarketplaceFormsReturn => {
  // Sale form state
  const [showBuyForm, setShowBuyForm] = useState(false);
  const [showConfirmForm, setShowConfirmForm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    sharePrice: '',
    numberOfShares: ''
  });
  const [legalConfirmation, setLegalConfirmation] = useState(false);
  const [accuracyConfirmation, setAccuracyConfirmation] = useState(false);
  
  // Buy request form state
  const [showRequestToBuyForm, setShowRequestToBuyForm] = useState(false);
  const [showRequestConfirmForm, setShowRequestConfirmForm] = useState(false);
  const [requestData, setRequestData] = useState<FormData>({
    sharePrice: '',
    numberOfShares: ''
  });
  const [requestFundsConfirmation, setRequestFundsConfirmation] = useState(false);
  const [requestAccuracyConfirmation, setRequestAccuracyConfirmation] = useState(false);

  // Actions
  const openSaleForm = () => {
    setShowBuyForm(true);
  };

  const openBuyRequestForm = () => {
    setShowRequestToBuyForm(true);
  };

  const closeForms = () => {
    setShowBuyForm(false);
    setShowConfirmForm(false);
    setShowRequestToBuyForm(false);
    setShowRequestConfirmForm(false);
    resetForms();
  };

  // Form handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequestInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRequestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitInitialForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (legalConfirmation) {
      setShowBuyForm(false);
      setShowConfirmForm(true);
    }
  };

  const handleSubmitRequestForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (requestFundsConfirmation) {
      setShowRequestToBuyForm(false);
      setShowRequestConfirmForm(true);
    }
  };

  // Generate unique ID
  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  // Final submission handlers
  const submitSaleOffer = async (user: User, onAdd: (offer: SaleOffer) => Promise<void>) => {
    if (!accuracyConfirmation) return;

    const newOffer: SaleOffer = {
      id: generateId(),
      sharePrice: formData.sharePrice,
      numberOfShares: formData.numberOfShares,
      timestamp: new Date().toISOString(),
      confirmationTimestamp: new Date().toISOString(),
      legalOwnershipConfirmed: legalConfirmation,
      accuracyConfirmed: accuracyConfirmation,
      ipAddress: '192.168.1.100', // Mock IP address
      country: user.country || 'Unknown',
      isp: 'Unknown ISP',
      seller: user.id,
      showFullName: true
    };

    await onAdd(newOffer);
    closeForms();
  };

  const submitBuyRequest = async (user: User, onAdd: (request: BuyRequest) => Promise<void>) => {
    if (!requestAccuracyConfirmation) return;

    const newRequest: BuyRequest = {
      id: generateId(),
      sharePrice: requestData.sharePrice,
      numberOfShares: requestData.numberOfShares,
      requestTimestamp: new Date().toISOString(),
      confirmationTimestamp: new Date().toISOString(),
      sufficientFundsConfirmed: requestFundsConfirmation,
      accuracyConfirmed: requestAccuracyConfirmation,
      ipAddress: '192.168.1.100', // Mock IP address
      country: user.country || 'Unknown',
      isp: 'Unknown ISP',
      requester: user.id,
      showFullName: true
    };

    await onAdd(newRequest);
    closeForms();
  };

  // Reset forms
  const resetForms = () => {
    setFormData({ sharePrice: '', numberOfShares: '' });
    setRequestData({ sharePrice: '', numberOfShares: '' });
    setLegalConfirmation(false);
    setAccuracyConfirmation(false);
    setRequestFundsConfirmation(false);
    setRequestAccuracyConfirmation(false);
  };

  return {
    // Sale form state
    showBuyForm,
    showConfirmForm,
    formData,
    legalConfirmation,
    accuracyConfirmation,
    
    // Buy request form state
    showRequestToBuyForm,
    showRequestConfirmForm,
    requestData,
    requestFundsConfirmation,
    requestAccuracyConfirmation,
    
    // Actions
    openSaleForm,
    openBuyRequestForm,
    closeForms,
    
    // Form handlers
    handleInputChange,
    handleRequestInputChange,
    handleSubmitInitialForm,
    handleSubmitRequestForm,
    
    // Confirmation handlers
    setLegalConfirmation,
    setAccuracyConfirmation,
    setRequestFundsConfirmation,
    setRequestAccuracyConfirmation,
    
    // Final submission
    submitSaleOffer,
    submitBuyRequest,
    
    // Reset functions
    resetForms
  };
}; 